import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, MapPin, Globe, ExternalLink, Phone, MessageCircle, Key, User, Calendar, Plus, Pencil, Trash2, Mail, HeartPulse } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { supabase } from "@/integrations/supabase/client";
import {
  useEstablishmentFollowup,
  useEstablishmentVisits,
  useEstablishmentNotes,
  useEstablishmentManagerEmail,
  VisitRow,
  NoteRow,
} from "@/hooks/useClientFollowup";
import { VisitDialog } from "@/components/admin/client-followup/VisitDialog";
import { NoteDialog } from "@/components/admin/client-followup/NoteDialog";
import { PiktableLoader } from "@/components/PiktableLoader";

const visitTypeLabels: Record<string, string> = {
  technical_audit: "Audit technique",
  commercial: "Commerciale",
  training: "Formation",
  client_relation: "Relation client",
  urgent_issue: "Problème urgent",
  field_followup: "Suivi terrain",
};
const statusLabels: Record<string, { label: string; cls: string }> = {
  planned: { label: "Planifiée", cls: "bg-blue-500/10 text-blue-700" },
  done: { label: "Effectuée", cls: "bg-green-500/10 text-green-700" },
  cancelled: { label: "Annulée", cls: "bg-muted text-muted-foreground" },
  to_reschedule: { label: "À replanifier", cls: "bg-amber-500/10 text-amber-700" },
};
const noteTypeLabels: Record<string, string> = {
  call: "Appel", whatsapp: "WhatsApp", email: "Email", visit: "Visite",
  meeting: "Réunion", followup: "Suivi", other: "Autre",
};

export default function ClientFollowupDetail() {
  const { establishmentId } = useParams<{ establishmentId: string }>();
  const navigate = useNavigate();

  const { data: establishment, isLoading: loadingEst } = useQuery({
    queryKey: ["establishment-detail", establishmentId],
    enabled: !!establishmentId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("establishments")
        .select("*")
        .eq("id", establishmentId!)
        .single();
      if (error) throw error;
      return data;
    },
  });

  const { data: followup, upsert } = useEstablishmentFollowup(establishmentId);
  const { data: visits, remove: removeVisit } = useEstablishmentVisits(establishmentId);
  const { data: notes, remove: removeNote } = useEstablishmentNotes(establishmentId);
  const { data: managerEmail } = useEstablishmentManagerEmail(establishmentId);

  const [contactDraft, setContactDraft] = useState<any>(null);
  const [accessDraft, setAccessDraft] = useState<any>(null);
  const [healthDraft, setHealthDraft] = useState<any>(null);
  const [visitDialogOpen, setVisitDialogOpen] = useState(false);
  const [editingVisit, setEditingVisit] = useState<VisitRow | null>(null);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [editingNote, setEditingNote] = useState<NoteRow | null>(null);

  if (loadingEst) return <PiktableLoader message="Chargement..." />;
  if (!establishment) return <div className="p-6">Établissement introuvable</div>;

  const contact = contactDraft ?? {
    manager_phone: followup?.manager_phone ?? "",
    has_whatsapp: followup?.has_whatsapp ?? false,
    last_contact_date: followup?.last_contact_date ?? "",
    last_contact_type: followup?.last_contact_type ?? "",
    last_contact_note: followup?.last_contact_note ?? "",
  };
  const access = accessDraft ?? {
    dashboard_url: followup?.dashboard_url ?? "",
    client_username: followup?.client_username ?? "",
    password_hint: followup?.password_hint ?? "",
  };
  const health = healthDraft ?? {
    client_health: followup?.client_health ?? "",
    priority: followup?.priority ?? "",
    recommended_frequency: followup?.recommended_frequency ?? "",
    health_reason: followup?.health_reason ?? "",
  };

  const saveContact = async () => {
    await upsert.mutateAsync({
      manager_phone: contact.manager_phone || null,
      has_whatsapp: contact.has_whatsapp,
      last_contact_date: contact.last_contact_date || null,
      last_contact_type: contact.last_contact_type || null,
      last_contact_note: contact.last_contact_note || null,
    });
    setContactDraft(null);
  };

  const saveAccess = async () => {
    await upsert.mutateAsync({
      dashboard_url: access.dashboard_url || null,
      client_username: access.client_username || null,
      password_hint: access.password_hint || null,
    });
    setAccessDraft(null);
  };

  const saveHealth = async () => {
    const { data: u } = await supabase.auth.getUser();
    await upsert.mutateAsync({
      client_health: health.client_health || null,
      priority: health.priority || null,
      recommended_frequency: health.recommended_frequency || null,
      health_reason: health.health_reason || null,
      health_updated_at: new Date().toISOString(),
      health_updated_by: u.user?.id ?? null,
    } as any);
    setHealthDraft(null);
  };

  const fullAddress = [establishment.address, establishment.postal_code, establishment.city, establishment.country]
    .filter(Boolean).join(", ");
  const bookingUrl = `https://piktable.ch/${establishment.slug}`;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
      <div className="container mx-auto p-6 max-w-5xl space-y-6">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/admin/client-followup")} aria-label="Retour">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-serif font-bold">{establishment.name}</h1>
            <p className="text-muted-foreground">Suivi client</p>
          </div>
        </div>

        {/* SECTION 1 — Identité */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" /> Fiche établissement
              <Badge variant="outline" className="ml-2 text-xs">Lecture seule</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div>
              <span className="text-muted-foreground">Adresse : </span>
              <span>{fullAddress || "—"}</span>
            </div>
            <div className="flex flex-wrap gap-3">
              {establishment.google_maps_url && (
                <a href={establishment.google_maps_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                  <MapPin className="w-4 h-4" /> Google Maps <ExternalLink className="w-3 h-3" />
                </a>
              )}
              {establishment.website_url && (
                <a href={establishment.website_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                  <Globe className="w-4 h-4" /> Site internet <ExternalLink className="w-3 h-3" />
                </a>
              )}
              <a href={bookingUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:underline">
                <Calendar className="w-4 h-4" /> Lien de réservation public <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          </CardContent>
        </Card>

        {/* SECTION — Diagnostic santé */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HeartPulse className="w-5 h-5" /> Diagnostic santé
              {followup?.health_updated_at && (
                <span className="ml-auto text-xs font-normal text-muted-foreground">
                  Mis à jour le {new Date(followup.health_updated_at).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" })}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Santé client</Label>
                <Select
                  value={health.client_health || ""}
                  onValueChange={(v) => setHealthDraft({ ...health, client_health: v })}
                >
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ok">OK</SelectItem>
                    <SelectItem value="watch">À surveiller</SelectItem>
                    <SelectItem value="risk">Risque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Priorité de suivi</Label>
                <Select
                  value={health.priority || ""}
                  onValueChange={(v) => setHealthDraft({ ...health, priority: v })}
                >
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high">Haute</SelectItem>
                    <SelectItem value="normal">Normale</SelectItem>
                    <SelectItem value="low">Basse</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Fréquence recommandée</Label>
                <Select
                  value={health.recommended_frequency || ""}
                  onValueChange={(v) => setHealthDraft({ ...health, recommended_frequency: v })}
                >
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Mensuelle</SelectItem>
                    <SelectItem value="quarterly">Trimestrielle</SelectItem>
                    <SelectItem value="biannual">Semestrielle</SelectItem>
                    <SelectItem value="annual">Annuelle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Raison / commentaire</Label>
              <Textarea
                rows={3}
                value={health.health_reason}
                onChange={(e) => setHealthDraft({ ...health, health_reason: e.target.value })}
                placeholder="Pourquoi cette évaluation ?"
              />
            </div>
            {healthDraft && (
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setHealthDraft(null)}>Annuler</Button>
                <Button onClick={saveHealth} disabled={upsert.isPending}>Enregistrer</Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* SECTION 2 — Contact interne */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" /> Contact principal interne
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Mail className="w-4 h-4" /> Email manager (lecture seule)</Label>
              <Input value={managerEmail ?? ""} readOnly disabled placeholder="Aucun manager trouvé" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Téléphone</Label>
                <Input
                  value={contact.manager_phone}
                  onChange={(e) => setContactDraft({ ...contact, manager_phone: e.target.value })}
                  placeholder="+41 ..."
                />
              </div>
              <div className="space-y-2 flex items-end gap-3 pb-2">
                <Switch
                  checked={contact.has_whatsapp}
                  onCheckedChange={(v) => setContactDraft({ ...contact, has_whatsapp: v })}
                />
                <Label className="flex items-center gap-1"><MessageCircle className="w-4 h-4" /> WhatsApp</Label>
              </div>
              <div className="space-y-2">
                <Label>Date du dernier contact</Label>
                <Input
                  type="date"
                  value={contact.last_contact_date}
                  onChange={(e) => setContactDraft({ ...contact, last_contact_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Type du dernier contact</Label>
                <Select
                  value={contact.last_contact_type || ""}
                  onValueChange={(v) => setContactDraft({ ...contact, last_contact_type: v })}
                >
                  <SelectTrigger><SelectValue placeholder="Sélectionner..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="visit">Visite</SelectItem>
                    <SelectItem value="call">Appel</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="other">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2 space-y-2">
                <Label>Note</Label>
                <Textarea
                  rows={2}
                  value={contact.last_contact_note}
                  onChange={(e) => setContactDraft({ ...contact, last_contact_note: e.target.value })}
                />
              </div>
            </div>
            {contactDraft && (
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setContactDraft(null)}>Annuler</Button>
                <Button onClick={saveContact} disabled={upsert.isPending}>Enregistrer</Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* SECTION 5 — Accès plateforme */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="w-5 h-5" /> Accès plateforme client
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>URL du dashboard client</Label>
              <Input
                value={access.dashboard_url}
                onChange={(e) => setAccessDraft({ ...access, dashboard_url: e.target.value })}
                placeholder={`https://piktable.ch/${establishment.slug}/dashboard`}
              />
            </div>
            <div className="space-y-2">
              <Label>Identifiant</Label>
              <Input
                value={access.client_username}
                onChange={(e) => setAccessDraft({ ...access, client_username: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Indice / emplacement du mot de passe</Label>
              <Input
                value={access.password_hint}
                onChange={(e) => setAccessDraft({ ...access, password_hint: e.target.value })}
                placeholder="ex: 1Password coffre Piktable"
              />
              <p className="text-xs text-muted-foreground">
                ⚠️ Aucun mot de passe n'est stocké ici. Renseignez l'emplacement où il est conservé.
              </p>
            </div>
            {accessDraft && (
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={() => setAccessDraft(null)}>Annuler</Button>
                <Button onClick={saveAccess} disabled={upsert.isPending}>Enregistrer</Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* SECTION 7 — Visites terrain */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2"><Calendar className="w-5 h-5" /> Visites terrain</span>
              <Button size="sm" onClick={() => { setEditingVisit(null); setVisitDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-1" /> Ajouter
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(visits || []).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">Aucune visite enregistrée</p>
            )}
            {(visits || []).map((v) => (
              <div key={v.id} className="border rounded-lg p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{new Date(v.visit_date).toLocaleDateString("fr-CH")}</span>
                      {!v.is_full_day && v.start_time && (
                        <span className="text-sm text-muted-foreground">{v.start_time.slice(0,5)}{v.end_time ? ` – ${v.end_time.slice(0,5)}` : ""}</span>
                      )}
                      <Badge variant="outline" className={statusLabels[v.status].cls}>{statusLabels[v.status].label}</Badge>
                      <Badge variant="outline">{visitTypeLabels[v.visit_type]}</Badge>
                    </div>
                    {v.summary && <p className="text-sm mt-1">{v.summary}</p>}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => { setEditingVisit(v); setVisitDialogOpen(true); }}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => removeVisit.mutate(v.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                {(v.detailed_report || v.issues_detected || v.actions_to_do) && (
                  <div className="text-sm space-y-1 text-muted-foreground">
                    {v.detailed_report && <div><strong>Compte-rendu :</strong> {v.detailed_report}</div>}
                    {v.issues_detected && <div><strong>Problèmes :</strong> {v.issues_detected}</div>}
                    {v.actions_to_do && <div><strong>Actions :</strong> {v.actions_to_do}</div>}
                  </div>
                )}
                {v.needs_followup && v.next_action_at && (
                  <div className="text-sm text-amber-700 bg-amber-500/10 px-2 py-1 rounded flex items-center gap-2 flex-wrap">
                    <span>
                      Prochaine action : {new Date(v.next_action_at).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" })}
                    </span>
                    {v.next_action_status && (
                      <Badge
                        variant={
                          v.next_action_status === "done"
                            ? "secondary"
                            : v.next_action_status === "in_progress"
                            ? "default"
                            : "outline"
                        }
                      >
                        {v.next_action_status === "todo"
                          ? "À faire"
                          : v.next_action_status === "in_progress"
                          ? "En cours"
                          : "Fait"}
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>

        {/* SECTION 8 — Notes chronologiques */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Notes chronologiques</span>
              <Button size="sm" onClick={() => { setEditingNote(null); setNoteDialogOpen(true); }}>
                <Plus className="w-4 h-4 mr-1" /> Ajouter
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {(notes || []).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">Aucune note enregistrée</p>
            )}
            {(notes || []).map((n) => (
              <div key={n.id} className="border-l-2 border-primary/30 pl-4 py-2 space-y-1">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline">{noteTypeLabels[n.note_type]}</Badge>
                      <span className="text-xs text-muted-foreground">
                        {new Date(n.note_datetime).toLocaleString("fr-CH", { dateStyle: "short", timeStyle: "short" })}
                      </span>
                    </div>
                    <p className="font-medium mt-1">{n.title}</p>
                    {n.content && <p className="text-sm text-muted-foreground whitespace-pre-wrap">{n.content}</p>}
                    {n.next_action && (
                      <p className="text-sm text-amber-700 mt-1">
                        → {n.next_action}{n.next_action_date ? ` (${new Date(n.next_action_date).toLocaleDateString("fr-CH")})` : ""}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => { setEditingNote(n); setNoteDialogOpen(true); }}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => removeNote.mutate(n.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      {establishmentId && (
        <>
          <VisitDialog
            open={visitDialogOpen}
            onOpenChange={setVisitDialogOpen}
            establishmentId={establishmentId}
            visit={editingVisit}
          />
          <NoteDialog
            open={noteDialogOpen}
            onOpenChange={setNoteDialogOpen}
            establishmentId={establishmentId}
            note={editingNote}
          />
        </>
      )}
    </div>
  );
}
